@layer theme, base, components, utilities;

@import "./reset.css" layer(base);

@import "@fontsource-variable/roboto-flex" layer(theme);
@import "@fontsource-variable/roboto-mono" layer(theme);
@import "tailwindcss/theme" layer(theme);

@import "./light.css" layer(theme) (prefers-color-scheme: light);
@import "./dark.css" layer(theme) (prefers-color-scheme: dark);

@import "tailwindcss/utilities" layer(utilities);

:root {
  color-scheme: light dark;

  --md-ref-typeface-brand: "Roboto Flex Variable";
  --md-ref-typeface-plain: "Roboto Flex Variable";

  --md-sys-shape-corner-none: 0;
  --md-sys-shape-corner-extra-small: 4px;
  --md-sys-shape-corner-small: 8px;
  --md-sys-shape-corner-medium: 12px;
  --md-sys-shape-corner-large: 16px;
  --md-sys-shape-corner-extra-large: 28px;
  --md-sys-shape-corner-full: 9999px;
}

@theme {
  /* Overrides */

  --font-\*: initial;
  --font-sans: "Roboto Flex Variable", "sans-serif";
  --font-mono: "Roboto Mono Variable", "monospace";

  --breakpoint-\*: initial;
  --breakpoint-medium: 600px;
  --breakpoint-expanded: 840px;
  --breakpoint-large: 1200px;
  --breakpoint-extra-large: 1600px;

  --radius-\*: initial;
  --radius-none: var(--md-sys-shape-corner-none);
  --radius-xs: var(--md-sys-shape-corner-extra-small);
  --radius-sm: var(--md-sys-shape-corner-small);
  --radius-md: var(--md-sys-shape-corner-medium);
  --radius-lg: var(--md-sys-shape-corner-large);
  --radius-xl: var(--md-sys-shape-corner-extra-large);
  --radius-full: var(--md-sys-shape-corner-full);

  --color-\*: initial;
  --color-md-primary: var(--md-sys-color-primary);
  --color-md-surface-tint: var(--md-sys-color-surface-tint);
  --color-md-on-primary: var(--md-sys-color-on-primary);
  --color-md-primary-container: var(--md-sys-color-primary-container);
  --color-md-on-primary-container: var(--md-sys-color-on-primary-container);
  --color-md-secondary: var(--md-sys-color-secondary);
  --color-md-on-secondary: var(--md-sys-color-on-secondary);
  --color-md-secondary-container: var(--md-sys-color-secondary-container);
  --color-md-on-secondary-container: var(--md-sys-color-on-secondary-container);
  --color-md-tertiary: var(--md-sys-color-tertiary);
  --color-md-on-tertiary: var(--md-sys-color-on-tertiary);
  --color-md-tertiary-container: var(--md-sys-color-tertiary-container);
  --color-md-on-tertiary-container: var(--md-sys-color-on-tertiary-container);
  --color-md-error: var(--md-sys-color-error);
  --color-md-on-error: var(--md-sys-color-on-error);
  --color-md-error-container: var(--md-sys-color-error-container);
  --color-md-on-error-container: var(--md-sys-color-on-error-container);
  --color-md-background: var(--md-sys-color-background);
  --color-md-on-background: var(--md-sys-color-on-background);
  --color-md-surface: var(--md-sys-color-surface);
  --color-md-on-surface: var(--md-sys-color-on-surface);
  --color-md-surface-variant: var(--md-sys-color-surface-variant);
  --color-md-on-surface-variant: var(--md-sys-color-on-surface-variant);
  --color-md-outline: var(--md-sys-color-outline);
  --color-md-outline-variant: var(--md-sys-color-outline-variant);
  --color-md-shadow: var(--md-sys-color-shadow);
  --color-md-scrim: var(--md-sys-color-scrim);
  --color-md-inverse-surface: var(--md-sys-color-inverse-surface);
  --color-md-inverse-on-surface: var(--md-sys-color-inverse-on-surface);
  --color-md-inverse-primary: var(--md-sys-color-inverse-primary);
  --color-md-primary-fixed: var(--md-sys-color-primary-fixed);
  --color-md-on-primary-fixed: var(--md-sys-color-on-primary-fixed);
  --color-md-primary-fixed-dim: var(--md-sys-color-primary-fixed-dim);
  --color-md-on-primary-fixed-variant: var(
    --md-sys-color-on-primary-fixed-variant
  );
  --color-md-secondary-fixed: var(--md-sys-color-secondary-fixed);
  --color-md-on-secondary-fixed: var(--md-sys-color-on-secondary-fixed);
  --color-md-secondary-fixed-dim: var(--md-sys-color-secondary-fixed-dim);
  --color-md-on-secondary-fixed-variant: var(
    --md-sys-color-on-secondary-fixed-variant
  );
  --color-md-tertiary-fixed: var(--md-sys-color-tertiary-fixed);
  --color-md-on-tertiary-fixed: var(--md-sys-color-on-tertiary-fixed);
  --color-md-tertiary-fixed-dim: var(--md-sys-color-tertiary-fixed-dim);
  --color-md-on-tertiary-fixed-variant: var(
    --md-sys-color-on-tertiary-fixed-variant
  );
  --color-md-surface-dim: var(--md-sys-color-surface-dim);
  --color-md-surface-bright: var(--md-sys-color-surface-bright);
  --color-md-surface-container-lowest: var(
    --md-sys-color-surface-container-lowest
  );
  --color-md-surface-container-low: var(--md-sys-color-surface-container-low);
  --color-md-surface-container: var(--md-sys-color-surface-container);
  --color-md-surface-container-high: var(--md-sys-color-surface-container-high);
  --color-md-surface-container-highest: var(
    --md-sys-color-surface-container-highest
  );

  --color-tvrn-gray: light-dark(oklch(85% 0 0), oklch(40% 0 0));
  --color-tvrn-on-gray: light-dark(oklch(24% 0 0), oklch(89% 0 0));
  --color-tvrn-mauve: light-dark(
    oklch(85% 0.07 calc(0turn / 7)),
    oklch(40% 0.06 calc(0turn / 7))
  );
  --color-tvrn-on-mauve: light-dark(
    oklch(24% 0.04 calc(0turn / 7)),
    oklch(89% 0.05 calc(0turn / 7))
  );
  --color-tvrn-orange: light-dark(
    oklch(85% 0.07 calc(1turn / 7)),
    oklch(40% 0.06 calc(1turn / 7))
  );
  --color-tvrn-on-orange: light-dark(
    oklch(24% 0.04 calc(1turn / 7)),
    oklch(89% 0.05 calc(1turn / 7))
  );
  --color-tvrn-bronze: light-dark(
    oklch(85% 0.07 calc(2turn / 7)),
    oklch(40% 0.06 calc(2turn / 7))
  );
  --color-tvrn-on-bronze: light-dark(
    oklch(24% 0.04 calc(2turn / 7)),
    oklch(89% 0.05 calc(2turn / 7))
  );
  --color-tvrn-green: light-dark(
    oklch(85% 0.07 calc(3turn / 7)),
    oklch(40% 0.06 calc(3turn / 7))
  );
  --color-tvrn-on-green: light-dark(
    oklch(24% 0.04 calc(3turn / 7)),
    oklch(89% 0.05 calc(3turn / 7))
  );
  --color-tvrn-teal: light-dark(
    oklch(85% 0.07 calc(4turn / 7)),
    oklch(40% 0.06 calc(4turn / 7))
  );
  --color-tvrn-on-teal: light-dark(
    oklch(24% 0.04 calc(4turn / 7)),
    oklch(89% 0.05 calc(4turn / 7))
  );
  --color-tvrn-blue: light-dark(
    oklch(85% 0.07 calc(5turn / 7)),
    oklch(40% 0.06 calc(5turn / 7))
  );
  --color-tvrn-on-blue: light-dark(
    oklch(24% 0.04 calc(5turn / 7)),
    oklch(89% 0.05 calc(5turn / 7))
  );
  --color-tvrn-lavender: light-dark(
    oklch(85% 0.07 calc(6turn / 7)),
    oklch(40% 0.06 calc(6turn / 7))
  );
  --color-tvrn-on-lavender: light-dark(
    oklch(24% 0.04 calc(6turn / 7)),
    oklch(89% 0.05 calc(6turn / 7))
  );

  --ease-\*: initial;

  --ease-expressive-fast-spatial: cubic-bezier(0.42, 1.67, 0.21, 0.9);
  --ease-expressive-fast-effects: cubic-bezier(0.31, 0.94, 0.34, 1);
  --ease-expressive-default-spatial: cubic-bezier(0.38, 1.21, 0.22, 1);
  --ease-expressive-default-effects: cubic-bezier(0.34, 0.8, 0.34, 1);
  --ease-expressive-slow-spatial: cubic-bezier(0.39, 1.29, 0.35, 0.98);
  --ease-expressive-slow-effects: cubic-bezier(0.34, 0.88, 0.34, 1);
  --ease-standard-fast-spatial: cubic-bezier(0.27, 1.06, 0.18, 1);
  --ease-standard-fast-effects: cubic-bezier(0.31, 0.94, 0.34, 1);
  --ease-standard-default-spatial: cubic-bezier(0.27, 1.06, 0.18, 1);
  --ease-standard-default-effects: cubic-bezier(0.34, 0.8, 0.34, 1);
  --ease-standard-slow-spatial: cubic-bezier(0.27, 1.06, 0.18, 1);
  --ease-standard-slow-effects: cubic-bezier(0.34, 0.88, 0.34, 1);

  --duration-expressive-fast-spatial: 350ms;
  --duration-expressive-fast-effects: 150ms;
  --duration-expressive-default-spatial: 500ms;
  --duration-expressive-default-effects: 200ms;
  --duration-expressive-slow-spatial: 650ms;
  --duration-expressive-slow-effects: 300ms;
  --duration-standard-slow-spatial: 750ms;
  --duration-standard-slow-effects: 300ms;
  --duration-standard-fast-spatial: 350ms;
  --duration-standard-fast-effects: 150ms;
  --duration-standard-default-spatial: 500ms;
  --duration-standard-default-effects: 200ms;

  /* Extensions */

  --grid-template-columns-fill-3: repeat(auto-fill, minmax(3rem, 1fr));
  --grid-template-columns-fill-5: repeat(auto-fill, minmax(5rem, 1fr));
  --grid-template-columns-fill-6: repeat(auto-fill, minmax(6rem, 1fr));

  --drop-shadow-primary: 0 0 4px rgb(118 90 11 / 0.2);

  --animate-update: bg-pop 500ms cubic-bezier(0.5, 0, 0.75, 0) forwards;

  @keyframes bg-pop {
    from {
      background-color: var(--md-sys-color-primary-container);
    }
  }

  --animate-cheer: cheer 8s linear 5s both infinite;

  @keyframes cheer {
    from,
    to {
      transform-origin: center right;
    }

    0%,
    10% {
      transform: translateY(0) rotate(0);
    }

    1% {
      transform: rotate(6deg);
    }

    3% {
      transform: rotate(-6deg);
    }

    4% {
      transform: rotate(3.6deg);
    }

    6% {
      transform: rotate(-2.4deg);
    }

    7% {
      transform: rotate(1.2deg);
    }
  }

  --animate-slide-in-blurred-bottom: slide-in-blurred-bottom
    var(--duration-standard-fast-spatial) var(--ease-standard-fast-spatial) both;

  @keyframes slide-in-blurred-bottom {
    0% {
      transform: translateY(1000px) scaleY(2.5) scaleX(0.2);
      transform-origin: 50% 100%;
      filter: blur(40px);
      opacity: 0;
    }

    100% {
      transform: translateY(0) scaleY(1) scaleX(1);
      transform-origin: 50% 50%;
      filter: blur(0);
      opacity: 1;
    }
  }

  --prose-body: var(--md-sys-color-on-surface);
  /* --prose-headings: var(--md-sys-color-primary); */
  /* --prose-lead: theme("colors.pink[700]"); */
  /* --prose-links: theme("colors.pink[900]"); */
  /* --prose-bold: theme("colors.pink[900]"); */
  /* --prose-counters: theme("colors.pink[600]"); */
  --prose-bullets: var(--md-sys-color-primary);
  /* --prose-hr": theme("colors.pink[300]"); */
  /* --prose-quotes": theme("colors.pink[900]"); */
  /* --prose-quote-borders": theme("colors.pink[300]"); */
  /* --prose-captions": theme("colors.pink[700]"); */
  /* --prose-code": theme("colors.pink[900]"); */
  /* --prose-pre-code": theme("colors.pink[100]"); */
  /* --prose-pre-bg": theme("colors.pink[900]"); */
  /* --prose-th-borders": theme("colors.pink[300]"); */
  /* --prose-td-borders": theme("colors.pink[200]"); */
}

@media not (prefers-reduced-motion) {
  @view-transition {
    navigation: auto;
  }

  @keyframes move-out {
    from {
      transform: translateX(0%);
    }

    to {
      transform: translateX(-100%);
    }
  }

  @keyframes move-in {
    from {
      transform: translateX(100%);
    }

    to {
      transform: translateX(0%);
    }
  }

  ::view-transition-old(root) {
    animation: 200ms ease-in both move-out;
  }

  ::view-transition-new(root) {
    animation: 200ms ease-in both move-in;
  }
}

@layer base {
  html {
    /* disable pull-to-refresh*/
    overscroll-behavior: contain;

    @apply bg-md-surface;
  }

  @media not (prefers-reduced-motion) {
    html {
      scroll-behavior: smooth;
    }
  }

  :root {
    interpolate-size: allow-keywords;
  }

  ::details-content {
    transition:
      height 0.5s ease,
      content-visibility 0.5s ease allow-discrete;
    height: 0;
    overflow: clip;
  }

  [open]::details-content {
    height: auto;
  }
}

@utility tvrn-menu-button-* {
  background-color: --value(--color-tvrn-*);
  color: --value(--color-tvrn-on-*);

  &:is(md-filled-button) {
    --md-sys-color-primary: --value(--color-tvrn-*);
    --md-sys-color-on-primary: --value(--color-tvrn-on-*);

    &::part(focus-ring) {
      --md-sys-color-secondary: light-dark(
        oklch(from --value(--color-tvrn-*) 48% 0.05 h),
        oklch(from --value(--color-tvrn-*) 82% 0.05 h)
      );
    }

    --md-filled-button-container-shape: var(--md-sys-shape-corner-medium);
    --md-filled-button-trailing-space: inherit;
    --md-filled-button-leading-space: inherit;

    min-width: 3rem;
    aspect-ratio: 1 / 1;
    padding: 0.5rem;
  }
}

/* doesn't override base utility */
@utility ease-* {
  /* also set duration when choosing an easing fn */
  transition-duration: --value(--duration-*);
}

@utility duration-* {
  /* also set duration when choosing an easing fn */
  transition-duration: --value(--duration-*);
}
